import "babel-polyfill";
import React from "react";
import { Router, Route, Switch, Redirect } from "react-static";
import { ThemeProvider, injectGlobal } from "styled-components";
import { IntlProvider, addLocaleData } from "react-intl";
import { connect, Provider } from "react-redux";
import { createStore, compose, applyMiddleware } from "redux";
import localization from "./localization";
import fi from "react-intl/locale-data/fi";
import en from "react-intl/locale-data/en";
import ru from "react-intl/locale-data/ru";
import { flattenMessages } from "./utils/flattenMessages";
import theme from "./theme/theme";
import reducers from "@flow/store/index";
import AppLayout from "@flow/layouts/AppLayout";
import { Navigation } from "@flow/components/navigation";
import Home from "@flow/scenes/home/Home";
import DefaultPage from "@flow/scenes/default/DefaultPage";
import Tickets from "@flow/scenes/tickets/Tickets";
import Artists from "@flow/scenes/artists/Artists";
import Foods from "@flow/scenes/foods/Foods";
import Arts from "@flow/scenes/arts/Arts";
import News from "@flow/scenes/news/News";
import PartnersPage from "@flow/scenes/partners/PartnersPage";
import {
  OVERLAY_TRANSITION_DURATION,
  COOKIE_POLICY_TOAST_TRANSITION_DURATION,
  ART_ENABLED,
  SCHEDULE_ENABLED,
  FAMILY_ENABLED,
  PROGRAM_ENABLED,
  TALKS_ENABLED,
} from "@flow/utils/constants";
import slickStyles from "slick-carousel/slick/slick.css";
import Page404 from "@flow/scenes/404";
import SchedulePage from "@flow/scenes/schedule/SchedulePage";
import Family from "@flow/scenes/family/Family";
import Talks from "@flow/scenes/talks/Talks";
import MyFlow from "@flow/scenes/my-flow/MyFlow";
import thunk from "redux-thunk";
import createSagaMiddleware from "redux-saga";
import { BASE_PATH } from "@flow/utils/helperUtils";
import indexSagas from "@flow/store/index-sagas";
import useWatchAuth from "./components/auth/use-watch-auth";

injectGlobal`
  @font-face {
    font-family: 'Flow-Inverted';
    src: url('${BASE_PATH}/assets/fonts/Flow2018-Inverted.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/Flow2018-Inverted.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/Flow2018-Inverted.woff2') format('woff2'), /* Super Modern Browsers */
    url('${BASE_PATH}/assets/fonts/Flow2018-Inverted.woff') format('woff') /* Pretty Modern Browsers */
  }
  
  @font-face {
    font-family: 'Flow-LightDisplay';
    src: url('${BASE_PATH}/assets/fonts/Flow2018-LightDisplay.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/Flow2018-LightDisplay.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/Flow2018-LightDisplay.woff2') format('woff2'), /* Super Modern Browsers */
    url('${BASE_PATH}/assets/fonts/Flow2018-LightDisplay.woff') format('woff') /* Pretty Modern Browsers */
  }
  
  @font-face {
    font-family: 'Flow-Text';
    src: url('${BASE_PATH}/assets/fonts/Flow2018-Text.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/Flow2018-Text.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/Flow2018-Text.woff2') format('woff2'), /* Super Modern Browsers */
    url('${BASE_PATH}/assets/fonts/Flow2018-Text.woff') format('woff') /* Pretty Modern Browsers */
  }
  
  @font-face {
    font-family: 'Flow-Poster-Bold';
    src: url('${BASE_PATH}/assets/fonts/FlowPoster-Bold-20220803.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/FlowPoster-Bold-20220803.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/FlowPoster-Bold-20220803.woff2') format('woff2'), /* Super Modern Browsers */
    url('${BASE_PATH}/assets/fonts/FlowPoster-Bold-20220803.woff') format('woff') /* Pretty Modern Browsers */
  }
  
  @font-face {
    font-family: 'Flow-Cyrillic';
    src: url('${BASE_PATH}/assets/fonts/Flow-Cyrillic.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/Flow-Cyrillic.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/Flow-Cyrillic.woff') format('woff') /* Pretty Modern Browsers */
  }
  
  @font-face {
    font-family: 'Flow-Text-Italic';
    src: url('${BASE_PATH}/assets/fonts/Flow2018-TextItalic.eot'); /* IE9 Compat Modes */
    src: url('${BASE_PATH}/assets/fonts/Flow2018-TextItalic.eot?#iefix') format('embedded-opentype'), /* IE6-IE8 */
    url('${BASE_PATH}/assets/fonts/Flow2018-TextItalic.woff') format('woff') /* Pretty Modern Browsers */
  }

  body {
    font-family: 'Flow-Text', sans-serif;
    font-size: 62.5%;
    text-size-adjust: 100%;
    text-rendering: optimizeLegibility;
    -webkit-tap-highlight-color: transparent;
    color: black;
  }
  
  .content {
    background-color: white;
  }
  
  b, strong {
    font-family: Flow-Poster-Bold;
  }
  
  .dingbats {
    font-family: Flow-Poster-Bold !important;
  }
  
  i, em {
    font-family: Flow-Text-Italic;
  }
  
  a {
    outline: none;
    color: black;
    cursor: pointer;
    text-decoration: none;
  }
  
  .cookie-privacy-toast-enter {
    transform: translateY(100%);
  }
  
  .cookie-privacy-toast-enter.cookie-privacy-toast-enter-active {
    transform: translateY(0);
    transition: ${COOKIE_POLICY_TOAST_TRANSITION_DURATION}ms;
  }
  
  .cookie-privacy-toast-leave {
    transform: translateY(0);
  }
  
  .cookie-privacy-toast-leave.cookie-privacy-toast-leave-active {
    transform: translateY(100%);
    transition: ${COOKIE_POLICY_TOAST_TRANSITION_DURATION}ms;
  }
  
  .overlay-enter { // defines the beginning state of the enter transition
    opacity: 0.01;
  }
  
  .overlay-enter.overlay-enter-active { // defines the actual enter transition
    opacity: 1;
    transition: ${OVERLAY_TRANSITION_DURATION}ms;
  }

  .overlay-leave { // defines the beginning state of the leave transition
    opacity: 1;
  }

  .overlay-leave.overlay-leave-active { // defines the actual leave transition
    opacity: 0.01;
    transition: ${OVERLAY_TRANSITION_DURATION}ms;
  }
  
  &.no-scroll {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  
  ${slickStyles}
  
  .slick-slider .slick-slide {
    display: none;
  }
  
  .slick-slider .slick-slide:first-child {
    display: block;
  }
  
  .slick-initialized.slick-slider .slick-slide,
  .slick-initialized.slick-slider .slick-slide:first-child {
    display: block;
  } 
`;

const composeSetup =
  process.env.NODE_ENV !== "production" &&
  typeof window === "object" &&
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

const sagaMiddleware: any = createSagaMiddleware();

const store = createStore(
  reducers,
  composeSetup(),
  applyMiddleware(...[thunk, sagaMiddleware])
);

sagaMiddleware.run(indexSagas);

addLocaleData([...fi, ...en, ...ru]);

interface Props {
  appLanguage: IAppLanguage;
}
export function FlowApp({ appLanguage }: Props) {
  const { code, tag } = appLanguage;
  const locale: string = code === "fi" ? `${code}-${code.toUpperCase()}` : tag;
  useWatchAuth();
  return (
    <IntlProvider
      key={code}
      locale={locale}
      messages={flattenMessages(localization[locale])}
    >
      <ThemeProvider theme={theme}>
        <Router>
          <AppLayout className={code === "ru" ? "use-cyrillic-font" : ""}>
            <Navigation />
            <div className="content">
              <Switch>
                /* FI */
                <Route exact strict path="/" component={Home} />
                <Route path="/festivaali/my-helsinki" component={DefaultPage} />
                <Route path="/festivaali/food" component={Foods} />
                <Route
                  path="/festivaali/sustainable-flow"
                  component={DefaultPage}
                />
                <Route path="/festivaali/makiaxflow" component={DefaultPage} />
                <Route path="/festivaali" component={DefaultPage} />
                <Route path="/flow-info/lavat" component={DefaultPage} />
                <Route path="/flow-info/faq" component={DefaultPage} />
                <Route path="/flow-info/saapuminen" component={DefaultPage} />
                <Route
                  path="/flow-info/rannekkeenvaihto"
                  component={DefaultPage}
                />
                <Route path="/flow-info" component={DefaultPage} />
                <Route path="/liput" component={Tickets} />
                {PROGRAM_ENABLED && (
                  <Route path="/artists" component={Artists} />
                )}
                {TALKS_ENABLED && <Route path="/talks" component={Talks} />}
                {ART_ENABLED && <Route path="/taide" component={Arts} />}
                {FAMILY_ENABLED && <Route path="/perhe" component={Family} />}
                {SCHEDULE_ENABLED && (
                  <Route path={"/aikataulu"} component={SchedulePage} />
                )}
                {SCHEDULE_ENABLED && (
                  <Route path={"/my-flow"} component={MyFlow} />
                )}
                <Route path="/news" component={News} />
                <Route path="/media" component={DefaultPage} />
                <Route path="/kumppanit" component={PartnersPage} />
                <Route path="/yhteystiedot" component={DefaultPage} />
                <Route path="/artist-contacts" component={DefaultPage} />
                <Route path="/vapaaehtoisinfo" component={DefaultPage} />
                <Route path="/cookies-policy" component={DefaultPage} />
                <Route path="/tietosuojaseloste" component={DefaultPage} />
                <Route path="/residentinfo" component={DefaultPage} />
                <Route path="/historia" component={DefaultPage} />
                /* EN */
                <Route exact path="/en" component={Home} />
                <Route
                  path="/en/festival/my-helsinki"
                  component={DefaultPage}
                />
                <Route path="/en/festival/food" component={Foods} />
                <Route
                  path="/en/festival/sustainable-flow"
                  component={DefaultPage}
                />
                <Route path="/en/festival/makiaxflow" component={DefaultPage} />
                <Route path="/en/festival" component={DefaultPage} />
                <Route path="/en/flow-info/venues" component={DefaultPage} />
                <Route path="/en/flow-info/faq" component={DefaultPage} />
                <Route
                  path="/en/flow-info/getting-there"
                  component={DefaultPage}
                />
                <Route
                  path="/en/flow-info/wristband-exchange/"
                  component={DefaultPage}
                />
                <Route path="/en/flow-info" component={DefaultPage} />
                <Route path="/en/tickets" component={Tickets} />
                {PROGRAM_ENABLED && (
                  <Route path="/en/artists" component={Artists} />
                )}
                {ART_ENABLED && <Route path="/en/art" component={Arts} />}
                {TALKS_ENABLED && <Route path="/en/talks" component={Talks} />}
                {FAMILY_ENABLED && (
                  <Route path="/en/family" component={Family} />
                )}
                {SCHEDULE_ENABLED && (
                  <Route path={"/en/schedule"} component={SchedulePage} />
                )}
                {SCHEDULE_ENABLED && (
                  <Route path={"/en/my-flow"} component={MyFlow} />
                )}
                <Route path="/en/news" component={News} />
                <Route path="/en/media" component={DefaultPage} />
                <Route path="/en/partners" component={PartnersPage} />
                <Route path="/en/contact" component={DefaultPage} />
                <Route path="/en/volunteer-info" component={DefaultPage} />
                <Route path="/en/cookies-policy" component={DefaultPage} />
                <Route path="/en/privacypolicy" component={DefaultPage} />
                <Route path="/en/history" component={DefaultPage} />
                <Redirect from="/tickets" to="/en/tickets" />
                /* RU */
                <Redirect from="/ru" to="/en" />
                <Redirect from="/ru/" to="/en" />
                {/*
                  <Route exact path='/ru' component={Home}/>

                  <Route path='/ru/festival/my-helsinki' component={DefaultPage}/>
                  <Route path='/ru/festival/food' component={Foods}/>
                  <Route path='/ru/festival/sustainable-flow' component={DefaultPage}/>
                  <Route path='/ru/festival/makiaxflow' component={DefaultPage}/>
                  <Route path='/ru/festival' component={DefaultPage}/>

                  <Route path='/ru/flow-info/venues' component={DefaultPage}/>
                  <Route path='/ru/flow-info/faq' component={DefaultPage}/>
                  <Route path='/ru/flow-info/getting-there' component={DefaultPage}/>
                  <Route path='/ru/flow-info/wristband-exchange/' component={DefaultPage}/>
                  <Route path='/ru/flow-info' component={DefaultPage}/>

                  <Route path='/ru/bilety' component={Tickets}/>

                  {ART_ENABLED && <Route path='/ru/art' component={Arts}/>}
                  {FAMILY_ENABLED && <Route path='/ru/family' component={Family}/>}
                  {SCHEDULE_ENABLED && <Route path={'/ru/schedule'} component={SchedulePage} />}
                  {SCHEDULE_ENABLED && <Route path={'/ru/my-flow'} component={MyFlow} />}

                  <Route path='/ru/news' component={News}/>

                  <Route path='/ru/media' component={DefaultPage}/>
                  <Route path='/ru/partners' component={PartnersPage}/>
                  <Route path='/ru/contact' component={DefaultPage}/>

                  <Route path='/ru/volunteer-info' component={DefaultPage}/>

                  <Route path='/ru/cookies-policy' component={DefaultPage}/>
                  <Route path='/ru/privacypolicy' component={DefaultPage}/>

                  <Redirect from='/bilety' to='/ru/bilety'/>
                  <Redirect from='/ru/artists' to='/en/artists'/>
                  */}
                /* DE route redirect */
                <Redirect from="/de" to="/en" />
                <Redirect from="/de/" to="/en" />
                <Route component={Page404} />
              </Switch>
            </div>
          </AppLayout>
        </Router>
      </ThemeProvider>
    </IntlProvider>
  );
}

function mapStateToProps({ navigationReducer }: any) {
  const { appLanguage } = navigationReducer;

  return { appLanguage };
}

const ConnectedFlowApp = connect(mapStateToProps)(FlowApp);

export default () => (
  <Provider store={store}>
    <ConnectedFlowApp />
  </Provider>
);



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/App.tsx